import type { Ref } from 'react';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import type { Game, MiniGameUiState } from '@/game/Game';
import type { UnlockId } from '@/core/content/ids';
import { LOCALES } from '@/core/i18n/translator';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { HudStats } from './HudStats';
import { PlayerAvatar } from './PlayerAvatar';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import { Icon } from './Icon';
import { SpeakerIcon } from './Icons';
import styles from './Hud.module.css';

interface Props {
  ref?: Ref<HTMLDivElement>;
  game: Game | null;
  onRecenter: () => void;
  onMenu: () => void;
  onMail: () => void;
  /** Only when "Nút Sếp tới!" is on in Settings. */
  onBoss?: () => void;
  /** "Sắp mở" → "Mở rộng" on a paid area. */
  onExpand?: (unlockId: UnlockId) => void;
  /** "Sắp mở" → "Xem tất cả". */
  onAllUnlocks?: () => void;
}

const IDLE = createStore<MiniGameUiState>(() => ({ run: null, intro: null }));

/** Milestone 0 HUD: title chip, VI/EN switch, recenter. Coins/Stars chips arrive with the economy (M2). */
export function Hud({ ref, game, onRecenter, onMenu, onMail, onBoss, onExpand, onAllUnlocks }: Props) {
  const { t, locale, setLocale } = useI18n();
  // Mini games have their own top bar; the room HUD steps aside (keeps its size for stable insets).
  const miniGame = useStore(game?.miniGameUi ?? IDLE, (s) => s.run !== null);
  return (
    <div
      ref={ref}
      className={styles.hud}
      data-testid="hud"
      style={miniGame ? { visibility: 'hidden' } : undefined}
    >
      <div className={styles.left}>
        <div className={styles.topRow}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={onMenu}
            aria-label={t('ui.menu.title')}
            data-testid="hud-menu"
          >
            <PlayerAvatar size={40} />
          </button>
          <div className={styles.titleChip}>
            <span className={styles.title}>{t('app.title')}</span>
            <span className={styles.tagline}>{t('app.tagline')}</span>
          </div>
        </div>
        <HudStats onExpand={onExpand} {...(onAllUnlocks ? { onAllUnlocks } : {})} />
      </div>
      <div className={styles.actions}>
        {onBoss && (
          <button type="button" className={styles.bossChip} data-testid="hud-boss" onClick={onBoss}>
            <Icon name="boss" size={22} />
            {t('ui.boss.button')}
          </button>
        )}
        <MailButton onClick={onMail} />
        <SoundButton />
        <div className={styles.segmented} role="group" aria-label={t('ui.lang.switch_label')}>
          {LOCALES.map((loc) => (
            <button
              key={loc}
              type="button"
              className={styles.segment}
              aria-pressed={locale === loc}
              onClick={() => {
                setLocale(loc);
              }}
            >
              {t(`ui.lang.${loc}`)}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onRecenter}
          aria-label={t('ui.camera.recenter')}
        >
          <Icon name="recenter" size={28} />
        </button>
      </div>
    </div>
  );
}

/** Hòm thư with a count badge (hidden at 0 — no pressure, ux-screens §2.3). */
function MailButton({ onClick }: { onClick: () => void }) {
  const { t } = useI18n();
  const count = useSave((s) => s.mailbox.pending.length + s.mailbox.special.length);
  return (
    <button
      type="button"
      className={styles.iconButton}
      onClick={onClick}
      aria-label={count > 0 ? t('ui.mail.button_count', { count }) : t('ui.mail.title')}
      data-testid="hud-mail"
    >
      <Icon name="mail" size={28} />
      {count > 0 && <span className={styles.badge}>{count}</span>}
    </button>
  );
}

/** Loa (ux-screens §3.1): mutes everything at once, always on the HUD. Same setting as M and Cài đặt. */
function SoundButton() {
  const { t } = useI18n();
  const { dispatch } = useServices();
  const muted = useSave((s) => s.settings.muted);
  return (
    <button
      type="button"
      className={styles.iconButton}
      aria-pressed={muted}
      aria-label={t(muted ? 'ui.minigame.sound_on' : 'ui.minigame.sound_off')}
      data-testid="hud-sound"
      onClick={() => {
        dispatch({ type: 'updateSettings', patch: { muted: !muted } });
      }}
    >
      <SpeakerIcon muted={muted} />
    </button>
  );
}
